import {
    HttpClient,
    HttpHeaders,
    HttpParams,
    HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { serverApiMaxSetSize } from '../constants-types/constants';
import { SearchState } from '../core-data-model/SearchState.model';
import { UserAgentDetectUtilities } from '../core-utilities/UserAgentDetect.utility';
import { Util } from '../core-utilities/Util';
import { HttpHeaderContentType } from '../shared/Enums';
import { ConfigurationService } from '../system-configuration/Configuration.service';
import { HeaderService } from './Header.service';
import { LogoutMessage, MessagingService } from './Messaging.service';
import { PdfReference } from './PdfReference';

@Injectable({ providedIn: 'root' })
export class ApiService {
    constructor(
        private userAgentDetect: UserAgentDetectUtilities,
        private httpClient: HttpClient,
        private config: ConfigurationService,
        private util: Util,
        private messageService: MessagingService,
        private headerService: HeaderService
    ) {}

    get http() {
        return this.httpClient;
    }

    postForImage<D = any>(url: string, data: D) {
        return this.postForFile(url, data, HttpHeaderContentType.PNG, '*/*');
    }

    postResourceUrl<D = any>(
        url: string,
        data: D,
        resourceType: HttpHeaderContentType
    ) {
        return this.processResourceUrl(
            this.postForFile(url, data, resourceType),
            resourceType
        );
    }

    // POST: To upload file
    postFile<D = FormData, T = any>(
        url: string,
        formData: D,
        host?: string
    ): Promise<[T, HttpResponse<T>]> {
        return firstValueFrom(
            this.http.post<T>(
                this.getRequestUrl(url, host),
                formData,
                this.getFileOptions(HttpHeaderContentType.JSON)
            )
        ).then(
            (data) => this.processResponse<T>(data),
            this.handleErrorResponse('POST', url)
        );
    }

    // This post, patch code looks like it has a few minor issues
    post<D = any, T = any>(
        url: string,
        data: D,
        host?: string,
        customErrorHandler?: (HttpResponse) => any
    ): Promise<[T, HttpResponse<T>]> {
        // TODO: do we need to stringify our data?
        return firstValueFrom(
            this.http.post<T>(
                this.getRequestUrl(url, host),
                data,
                this.headerService.getOptions()
            )
        ).then(
            (responseData) => this.processResponse<T>(responseData),
            customErrorHandler || this.handleErrorResponse('POST', url)
        );
    }
    postBloblessResource<D = any, T = any>(
        url: string,
        data: D,
        host?: string
    ) {
        return firstValueFrom(
            this.http.post<T>(
                this.getRequestUrl(url, host),
                data,
                this.headerService.getOptions()
            )
        ).then(
            (response) => this.processResponse<T>(response),
            this.handleErrorResponse('POST', url)
        );
    }

    patch<D = any, T = any>(
        url: string,
        data: D,
        host?: string
    ): Promise<[T, HttpResponse<T>]> {
        return firstValueFrom(
            this.http.patch(
                this.getRequestUrl(url, host),
                data,
                this.headerService.getOptions()
            )
        ).then(this.processResponse, this.handleErrorResponse('PATCH', url));
    }

    put<D = any, T = any>(
        url: string,
        data: D,
        host?: string
    ): Promise<[T, HttpResponse<T>]> {
        return firstValueFrom(
            this.http.put(
                this.getRequestUrl(url, host),
                data,
                this.headerService.getOptions()
            )
        ).then(this.processResponse, this.handleErrorResponse('PUT', url));
    }

    delete(
        url: string,
        body?: any,
        host?: string
    ): Promise<[any, HttpResponse<any>] | void> {
        let request;

        if (body) {
            request = this.http.request(
                'delete',
                this.getRequestUrl(url, host),
                {
                    body
                }
            );
        } else {
            request = this.http.delete(
                this.getRequestUrl(url, host),
                this.headerService.getOptions()
            );
        }

        return firstValueFrom(request).then(
            (response) => response,
            this.handleErrorResponse('DELETE', url)
        );
    }

    head(url: string, host?: string): Promise<any> {
        const headoptions = this.headerService.getOptions('text');
        return firstValueFrom(
            this.http.head(this.getRequestUrl(url, host), headoptions)
        ).then(this.processHeadResponse, this.handleErrorResponse('HEAD', url));
    }

    /**
     *
     * @param url
     * @param data
     *
     * The returned value of [T, HttpResponse<T>] is to preserve the response
     * for the notification service. To get access to T directly, make
     * sure you use ([data]) +>  with the [].
     */
    get<T, D = null>(
        url: string,
        data: D = null,
        urlWithHost: string = null,
        host?: string
    ): Promise<[T, HttpResponse<T>]> {
        const options = this.headerService.getOptions();
        if (data) {
            let params: HttpParams = new HttpParams();
            for (const key in data) {
                // HttpParams is immutable, so set() returns a new instance
                params = params.set(key.toString(), data[key] as any);
            }
            options.params = params;
        }

        return firstValueFrom(
            this.http.get<T>(
                urlWithHost || this.getRequestUrl(url, host),
                options
            )
        ).then(
            (response) => this.processResponse<T>(response),
            this.handleErrorResponse('GET', url)
        );
    }

    getResourceUrl(
        url: string,
        contentType: HttpHeaderContentType,
        isPost = false,
        data: any = null,
        host: string = null
    ): Promise<Blob> {
        return this.extractBlobFromRequest(isPost, url, host, data).then(
            (blob) => new Blob([blob], { type: contentType })
        );
    }
    getResourceUrlWithPdfReference(
        url: string,
        contentType: HttpHeaderContentType,
        isPost = false,
        data: any = null,
        host: string = null
    ): Promise<PdfReference> {
        return this.extractBlobFromRequest(isPost, url, host, data).then(
            (blob) =>
                blob
                    ? PdfReference.createPdfReference(
                          new Blob([blob], { type: contentType })
                      )
                    : null
        );
    }

    getExternalPdfResource(url: string, contentType: HttpHeaderContentType) {
        return (
            firstValueFrom(
                this.httpClient.get(
                    url,
                    this.getBlobOptions(HttpHeaderContentType.JSON)
                )
            )
                // This cast is because "blob" is a Blob and the constructor expects a
                // BlobPart...which is a superset of BlobPart...no idea why TS 4.0 throws an error
                .then((blob) => new Blob([blob as any], { type: contentType }))
        );
    }

    processResourceUrl(
        promise: Promise<Blob>,
        contentType: HttpHeaderContentType
    ): Promise<BlobPart> {
        if (this.userAgentDetect.isIE()) {
            return promise.then((blob) => this.util.blobToArrayBuffer(blob));
        }
        return promise.then((blob) => {
            if (contentType === HttpHeaderContentType.PDF) {
                return this.util.blobToSafeResourceUrl(blob) as BlobPart;
            } else {
                return this.util.blobToSafeUrl(blob) as BlobPart;
            }
        });
    }

    multiplexSearchRequest<Result>(
        searchState: SearchState,
        fnToRun: (searchState: SearchState) => Promise<Result[]>
    ): Promise<Result[]> {
        const maxIndexPageSize = serverApiMaxSetSize;
        const maxResults =
            searchState.pageState.resultsPerPage || maxIndexPageSize;
        const pageFetches = Math.ceil(maxResults / maxIndexPageSize);
        const initialPageNumber = searchState.pageState.currentPageNumber;
        const pageSize =
            searchState.pageState.resultsPerPage < maxIndexPageSize
                ? searchState.pageState.resultsPerPage
                : maxIndexPageSize;
        return Promise.all(
            Array.from(Array(pageFetches).keys()).map((i) => {
                const newSearch = searchState
                    .setPage(initialPageNumber + i)
                    .setPageSize(pageSize);
                return fnToRun(newSearch);
            })
        ).then((setOfResults: Result[][]) =>
            setOfResults.reduce(
                (existing, current) => existing.concat(current),
                []
            )
        );
    }

    private postForFile<D = any>(
        url: string,
        data: D,
        resourceType: HttpHeaderContentType,
        acceptType?: string,
        host?: string
    ): Promise<Blob> {
        return firstValueFrom(
            this.http.post(
                this.getRequestUrl(url, host),
                data,
                this.getBlobOptions(acceptType)
            )
        ).then(
            (blob: any) => new Blob([blob.body], { type: resourceType }),
            this.handleErrorResponse('POST', url)
        );
    }

    private processHeadResponse<T>(response: HttpResponse<T>): HttpHeaders {
        return response.headers;
    }

    private processResponse<T>(
        response: HttpResponse<T>
    ): [T, HttpResponse<T>] {
        return [response.body, response];
    }

    // NOTE: Do not set ContentType. The client will interpret the Content-Type as Multipart and the necessary booundary
    // https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post
    private getFileOptions(acceptContentType?: string) {
        let headers = new HttpHeaders({
            Accept: acceptContentType || HttpHeaderContentType.JSON
        });
        headers = this.headerService.appendRequestingModuleHeader(
            headers,
            window.location.pathname
        );
        return {
            headers,
            withCredentials: true,
            observe: 'response' as const,
            params: {}
        };
    }
    // This used to be more elegant but the typing for the new angular HttpClient
    // sucks big time.
    private getBlobOptions(acceptContentType?: string) {
        return Object.assign(this.headerService.getOptions(acceptContentType), {
            responseType: 'blob' as const
        });
    }

    private handleErrorResponse(
        method: string,
        url: string
    ): (response: HttpResponse<any>) => any {
        return (response: HttpResponse<any>) => {
            if (response.status === 401) {
                // Unauthorized
                this.messageService.sendMessage(new LogoutMessage());
            }
            // Add some extra info to the error message about where it's from
            const customErrorMessage =
                `Request Error: ${method}` +
                ` ${url.split('?')[0]} \nRequest error message:` +
                ` ${response.toString()}`;
            response.toString = () => customErrorMessage;
            throw response;
        };
    }

    private getRequestUrl(url: string, host?: string): string {
        // if the host was provided then use that regardless of url
        // TODO: We need to figure out a better solution with the getResourceUrlExperiment having the default host assigned

        if (host) {
            return host + url;
        }
        return this.config.getUrlWithHost(url);
    }

    private extractBlobFromRequest(
        isPost: boolean,
        url: string,
        host: string,
        data: any
    ) {
        const options = this.getBlobOptions(HttpHeaderContentType.JSON);
        const response = isPost
            ? this.http.post(this.getRequestUrl(url, host), data, options)
            : this.http.get(this.getRequestUrl(url, host), options);
        // Http upgrade returns HTTPResponse instead of Blob; hence the weird cast
        return firstValueFrom(response).then(
            (responseData: any) => responseData.body as Blob
        );
    }
}
